import axios from "axios";
import i18n from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const WS_STORAGE_KEY = "lg_current_workspace_id";

// Cookies travel with every request; user_id is never sent from the client.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Inject X-Workspace-Id on every request when a current workspace is selected.
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const wsid = localStorage.getItem(WS_STORAGE_KEY);
    if (wsid) {
      config.headers = config.headers || {};
      config.headers["X-Workspace-Id"] = wsid;
    }
  }
  return config;
});

// One in-flight refresh promise to coalesce concurrent 401s into a single retry.
let refreshing = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Don't try to refresh on auth endpoints themselves
    const url = original?.url || "";
    const isAuthRoute = url.includes("/auth/");

    // Mandatory-2FA gate: a guarded route rejected this session because the
    // user must enroll. Signal the app shell to re-check session state so the
    // router can redirect to the forced-enrollment page.
    const mfaCode = error.response?.data?.detail?.code;
    if (status === 403 && mfaCode === "mfa_enrollment_required") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("mfa-enrollment-required"));
      }
      return Promise.reject(error);
    }
    // Email verification (2026-07-30). Unlike the MFA case there is no
    // redirect: the user stays where they are, the banner is already on
    // screen, and only this one action failed. We re-fetch /auth/me so a
    // stale "ok" status in the client corrects itself.
    if (status === 403 && mfaCode === "email_verification_required") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("email-verification-required"));
      }
      return Promise.reject(error);
    }
    // Sentinel plan gate (2026-08-23). The backend answers 403 on any
    // server-side-gated surface with { code: "plan_upgrade_required",
    // required_plan, current_plan, msg }. We turn that into an application
    // event so the shell can show the upgrade modal instead of a raw error
    // toast — a paywall is not an outage. The rejection still propagates so
    // existing per-call .catch() handling is unaffected.
    if (status === 403 && mfaCode === "plan_upgrade_required") {
      if (typeof window !== "undefined") {
        const detail = error.response?.data?.detail || {};
        window.dispatchEvent(new CustomEvent("qenva:plan-upgrade-required", {
          detail: { requiredPlan: detail.required_plan, currentPlan: detail.current_plan },
        }));
      }
      error.handledAsUpgrade = true;
      return Promise.reject(error);
    }

    if (status === 401 && !original._retried && !isAuthRoute) {
      original._retried = true;
      try {
        refreshing =
          refreshing ||
          api.post("/auth/refresh").finally(() => {
            refreshing = null;
          });
        await refreshing;
        return api(original);
      } catch (e) {
        // Refresh failed → bubble up so the AuthContext can sign out.
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return i18n.t("common:errors.generic");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
